import { useEffect, useState } from 'react';
import { ISO_STANDARDS } from '../../constants/isoStandards';
import { fetchRisks } from '../risks/risksApi';
import type { ChangeRequestPayload, ChangeRequestType } from './types';
import { CHANGE_TYPE_LABEL, STANDARD_LABEL } from './changeUtils';

const CHANGE_TYPES = Object.keys(CHANGE_TYPE_LABEL) as ChangeRequestType[];

interface ChangeRequestFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: ChangeRequestPayload) => Promise<void>;
  initial?: Partial<ChangeRequestPayload>;
  title: string;
  showRiskAssessment?: boolean;
}

function defaultPayload(): ChangeRequestPayload {
  return {
    title: '',
    description: '',
    changeType: 'proceso',
    affectedStandards: ['iso9001'],
    reason: '',
    linkedRiskIds: [],
  };
}

function toDateInput(iso?: string | null): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

export function ChangeRequestFormModal({
  open,
  onClose,
  onSubmit,
  initial,
  title,
  showRiskAssessment = false,
}: ChangeRequestFormModalProps) {
  const [form, setForm] = useState<ChangeRequestPayload>(defaultPayload());
  const [riskOptions, setRiskOptions] = useState<{ id: string; code: string; title: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm({
      ...defaultPayload(),
      ...initial,
      affectedStandards: initial?.affectedStandards ?? ['iso9001'],
      linkedRiskIds: initial?.linkedRiskIds ?? [],
    });
    setError(null);
    void fetchRisks({ pageSize: 200 }).then((res) => {
      setRiskOptions(res.data.map((r) => ({ id: r.id, code: r.code, title: r.title })));
    });
  }, [open, initial]);

  if (!open) return null;

  const toggleStandard = (standard: (typeof ISO_STANDARDS)[number]) => {
    setForm((prev) => {
      const has = prev.affectedStandards.includes(standard);
      const next = has
        ? prev.affectedStandards.filter((s) => s !== standard)
        : [...prev.affectedStandards, standard];
      return { ...prev, affectedStandards: next.length > 0 ? next : prev.affectedStandards };
    });
  };

  const toggleRisk = (riskId: string) => {
    setForm((prev) => {
      const current = prev.linkedRiskIds ?? [];
      const next = current.includes(riskId)
        ? current.filter((id) => id !== riskId)
        : [...current, riskId];
      return { ...prev, linkedRiskIds: next };
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSubmit(form);
      onClose();
    } catch {
      setError('No se pudo guardar la solicitud de cambio.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-lg border border-border bg-background p-6 shadow-lg max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-4">{title}</h2>
        <form className="space-y-4" onSubmit={(e) => void handleSubmit(e)}>
          <label className="block text-sm">
            Título
            <input
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              required
              maxLength={255}
              placeholder="Ej: Cambio de proveedor crítico"
            />
          </label>

          <label className="block text-sm">
            Tipo de cambio
            <select
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
              value={form.changeType}
              onChange={(e) =>
                setForm((p) => ({ ...p, changeType: e.target.value as ChangeRequestType }))
              }
            >
              {CHANGE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {CHANGE_TYPE_LABEL[type]}
                </option>
              ))}
            </select>
          </label>

          <fieldset className="text-sm">
            <legend className="mb-1">Normas afectadas</legend>
            <div className="flex flex-wrap gap-3">
              {ISO_STANDARDS.map((standard) => (
                <label key={standard} className="inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={form.affectedStandards.includes(standard)}
                    onChange={() => toggleStandard(standard)}
                  />
                  {STANDARD_LABEL[standard]}
                </label>
              ))}
            </div>
          </fieldset>

          <label className="block text-sm">
            Descripción del cambio
            <textarea
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm min-h-[80px]"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              required
              placeholder="Qué cambiará y en qué alcance…"
            />
          </label>

          <label className="block text-sm">
            Motivo / justificación
            <textarea
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm min-h-[60px]"
              value={form.reason}
              onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
              required
              placeholder="Por qué es necesario el cambio…"
            />
          </label>

          <label className="block text-sm">
            Fecha planificada
            <input
              type="date"
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
              value={toDateInput(form.plannedDate)}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  plannedDate: e.target.value ? new Date(e.target.value).toISOString() : null,
                }))
              }
            />
          </label>

          {showRiskAssessment && (
            <label className="block text-sm">
              Evaluación de riesgos
              <textarea
                className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm min-h-[60px]"
                value={form.riskAssessmentNote ?? ''}
                onChange={(e) =>
                  setForm((p) => ({ ...p, riskAssessmentNote: e.target.value || null }))
                }
                placeholder="Riesgos identificados y controles previstos…"
              />
            </label>
          )}

          <fieldset className="text-sm">
            <legend className="mb-1">Riesgos vinculados</legend>
            <div className="max-h-32 overflow-y-auto border border-border rounded-md p-2 space-y-1">
              {riskOptions.length === 0 ? (
                <p className="text-muted-foreground text-xs">Sin riesgos disponibles.</p>
              ) : (
                riskOptions.map((risk) => (
                  <label key={risk.id} className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={(form.linkedRiskIds ?? []).includes(risk.id)}
                      onChange={() => toggleRisk(risk.id)}
                    />
                    <span className="font-mono">{risk.code}</span> {risk.title}
                  </label>
                ))
              )}
            </div>
          </fieldset>

          {form.changeType === 'documento' && (
            <p className="text-xs text-muted-foreground">
              Para cambios documentales, actualizá el documento controlado en{' '}
              <a href="/documentos" className="text-primary underline">
                Documentos
              </a>{' '}
              una vez aprobado el cambio.
            </p>
          )}

          {error && <p className="text-destructive text-sm">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn btn-outline" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
